/**
 * VAT period keys (pure). A period key identifies the redovisningsperiod a
 * verifikation belongs to and is stored on `VerifikationMeta.periodKey` and
 * `PeriodLock.periodKey`.
 *
 *   month   -> 'YYYY-MM'
 *   quarter -> 'YYYY-Qn'          (calendar quarters, as Skatteverket uses)
 *   year    -> 'FY:<fiscalYearId>' (the beskattningsår is the fiscal year)
 *
 * Monthly and quarterly periods follow the calendar regardless of a broken
 * fiscal year; the first/last period of a fiscal year is clipped to its
 * bounds.
 */
import type { EntityType, FiscalYear, IsoDate, VatPeriod } from '../types';
import { addMonths, daysInMonth, declarationDeadline, parseIsoDate, toIsoDate } from './deadlines';

export interface VatPeriodInfo {
  key: string;
  from: IsoDate;
  to: IsoDate;
  deadline: IsoDate;
}

export const FISCAL_YEAR_KEY_PREFIX = 'FY:';

export function fiscalYearPeriodKey(fy: Pick<FiscalYear, 'id'>): string {
  return `${FISCAL_YEAR_KEY_PREFIX}${fy.id}`;
}

export function monthKey(date: IsoDate): string {
  return date.slice(0, 7);
}

export function quarterKey(date: IsoDate): string {
  const { year, month } = parseIsoDate(date);
  return `${year}-Q${Math.floor((month - 1) / 3) + 1}`;
}

export function periodKeyFor(date: IsoDate, fy: Pick<FiscalYear, 'id'>, period: VatPeriod): string {
  switch (period) {
    case 'month':
      return monthKey(date);
    case 'quarter':
      return quarterKey(date);
    case 'year':
      return fiscalYearPeriodKey(fy);
    default: {
      const exhaustive: never = period;
      throw new Error(`Unknown VAT period ${String(exhaustive)}`);
    }
  }
}

/** Calendar bounds of a 'YYYY-MM' or 'YYYY-Qn' key; null for fiscal-year keys or garbage. */
export function calendarBoundsOf(key: string): { from: IsoDate; to: IsoDate } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return { from: toIsoDate(year, month, 1), to: toIsoDate(year, month, daysInMonth(year, month)) };
  }
  const q = /^(\d{4})-Q([1-4])$/.exec(key);
  if (q) {
    const year = Number(q[1]);
    const first = (Number(q[2]) - 1) * 3 + 1;
    const last = first + 2;
    return { from: toIsoDate(year, first, 1), to: toIsoDate(year, last, daysInMonth(year, last)) };
  }
  return null;
}

/** Bounds of any period key within a fiscal year (clipped to the year). */
export function periodBounds(key: string, fy: Pick<FiscalYear, 'id' | 'startDate' | 'endDate'>): { from: IsoDate; to: IsoDate } | null {
  if (key === fiscalYearPeriodKey(fy)) return { from: fy.startDate, to: fy.endDate };
  const bounds = calendarBoundsOf(key);
  if (!bounds) return null;
  const from = bounds.from < fy.startDate ? fy.startDate : bounds.from;
  const to = bounds.to > fy.endDate ? fy.endDate : bounds.to;
  return from <= to ? { from, to } : null;
}

/**
 * Every VAT period that intersects the fiscal year, in order, with its
 * declaration deadline. `entity` only matters for yearly periods; the default
 * matches the most common yearly filer (enskild firma).
 */
export function vatPeriodKeys(
  fy: Pick<FiscalYear, 'id' | 'startDate' | 'endDate'>,
  period: VatPeriod,
  entity: EntityType = 'enskild_firma',
): VatPeriodInfo[] {
  if (period === 'year') {
    return [{ key: fiscalYearPeriodKey(fy), from: fy.startDate, to: fy.endDate, deadline: declarationDeadline(fy.endDate, period, entity) }];
  }
  const step = period === 'month' ? 1 : 3;
  const start = parseIsoDate(fy.startDate);
  const firstMonth = period === 'month' ? start.month : Math.floor((start.month - 1) / 3) * 3 + 1;
  const out: VatPeriodInfo[] = [];
  let cursor = { year: start.year, month: firstMonth };
  for (;;) {
    const from = toIsoDate(cursor.year, cursor.month, 1);
    if (from > fy.endDate) break;
    const lastMonth = addMonths(cursor, step - 1);
    const to = toIsoDate(lastMonth.year, lastMonth.month, daysInMonth(lastMonth.year, lastMonth.month));
    const key = period === 'month' ? monthKey(from) : quarterKey(from);
    const clippedFrom = from < fy.startDate ? fy.startDate : from;
    const clippedTo = to > fy.endDate ? fy.endDate : to;
    out.push({ key, from: clippedFrom, to: clippedTo, deadline: declarationDeadline(to, period, entity) });
    cursor = addMonths(cursor, step);
  }
  return out;
}
